import { useCallback, useEffect, useMemo, useRef } from 'react';
import { APOLLO_ERROR_POLICY, APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useGetSalesLazyQuery } from '@/publicSearch/graphql/queries.generated';
import { useStartSearchOnMount } from '../useStartSearchOnMount';
import { format } from 'date-fns';
import { saleCatalogueColumns } from '@/components/publicSearch/sale-catalogues/constants';
import {
  FieldOption,
  GetSalesRow,
  LogicalOperator,
  OperatorOption,
  SaleType,
} from '@/components/publicSearch/constants';
import type {
  ComparisonOperator,
  FilterGroupOfSaleFilteredFilterOptionInput,
  SaleFilterField,
  SaleFilteredSortOptionInput,
} from '@/graphqlClient/types';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import { SEARCH_TYPE } from '@/constants/searchType';
import useGridStore from '@/publicSearch/store/useGridStore';
import { getSearchPagination } from '@/lib/searchPagination';
import { useCatalogueFilters } from '../useCatalogueFilters';
import { useSearchType } from '../useSearchType';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { ISO_DATE_FORMAT } from '@/configs';
import { useSocietyDateFormat } from '@/hooks/publicSearch/useSocietyDisplayOptions';
import { SALE_DATE_FILTER, SaleDateFilter } from '@/constants/saleFilters';
import { buildCurrentSaleFilterGroup } from './buildCurrentSaleFilterGroup';
import { buildSaleSearchFilterGroup } from './buildSaleSearchFilterGroup';
import { buildSaleSortOptions } from './buildSaleSortOptions';

// Allowed sale types for display (exclude Z = draft, U = admin). L = listing, R = report/completed.
// Second character of a sale-type code marks the listing vs report variant.
const SALE_CODE_SUFFIX = {
  LISTING: 'L',
  REPORT: 'R',
} as const;
const LISTING_TYPES = [
  SaleType.CurrentSaleListing,
  SaleType.CurrentPrivateTreatySaleListing,
  SaleType.SemenListing,
  SaleType.EmbryoListing,
];
const REPORT_TYPES = [
  SaleType.CompletedSaleListing,
  SaleType.CompletedPrivateTreatySaleListing,
  SaleType.CompletedSemenListing,
  SaleType.CompletedEmbryoListing,
];
const ALL_ALLOWED_TYPES = [...LISTING_TYPES, ...REPORT_TYPES];

function typesForSaleTypeFilter(
  saleTypeFilterArray: string[],
  saleDateFilter: SaleDateFilter,
): string[] {
  if (saleTypeFilterArray.length === 0) {
    return saleDateFilter === SALE_DATE_FILTER.ALL
      ? ALL_ALLOWED_TYPES
      : LISTING_TYPES;
  }
  const result: string[] = [];
  for (const code of saleTypeFilterArray) {
    const firstLetter = code.charAt(0);
    const lType =
      code.charAt(1) === SALE_CODE_SUFFIX.LISTING
        ? code
        : ((firstLetter + SALE_CODE_SUFFIX.LISTING) as SaleType);
    const rType =
      code.charAt(1) === SALE_CODE_SUFFIX.REPORT
        ? code
        : ((firstLetter + SALE_CODE_SUFFIX.REPORT) as SaleType);
    if (ALL_ALLOWED_TYPES.includes(lType as SaleType)) result.push(lType);
    if (
      saleDateFilter === SALE_DATE_FILTER.ALL &&
      ALL_ALLOWED_TYPES.includes(rType as SaleType)
    ) {
      result.push(rType);
    }
  }
  if (result.length) {
    return result;
  }
  return saleDateFilter === SALE_DATE_FILTER.ALL
    ? ALL_ALLOWED_TYPES
    : LISTING_TYPES;
}

export function useSaleCatalogueFilter(enabled: boolean) {
  const orgId = usePublicSearchContext((context) => context.society.orgId);
  useSearchType();
  const { commonFilterOperator, filters } = useFilterStore(
    SEARCH_TYPE.SALE_CATALOGUES,
  );
  const { gridSortModel, paginationModel: storedPaginationModel } =
    useGridStore();
  const paginationModel = getSearchPagination(
    storedPaginationModel,
    SEARCH_TYPE.SALE_CATALOGUES,
  );
  const { saleDateFilter, saleTypeFilterArray } = useCatalogueFilters();
  const dateFormat = useSocietyDateFormat();

  const [getSales, { data, error, loading, previousData, refetch }] =
    useGetSalesLazyQuery({
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
      errorPolicy: APOLLO_ERROR_POLICY.ALL,
    });
  // See useAnimalEbvFilter for the rationale: route the *next* startSearch
  // through refetch() only when we have a tracked error, so the grid recovers
  // from a stuck ObservableQuery after errors / aborts without needing a
  // society-switch + apolloClient.resetStore().
  const recoveredFromErrorRef = useRef<unknown>(null);

  // Apollo's lazy-query handles (`getSales`, `refetch`) and the `error`
  // object can swap reference between the render that issues the first
  // execute and the render that commits the response — even when nothing
  // user-meaningful has changed. Listing them in startSearch's dependency
  // array is what produced the "loads, then refreshes the grid once" jank
  // on /sales: post-data render → new startSearch ref → useStartSearchOnMount
  // fires a duplicate execute → grid flips loading=true → loading=false
  // again. Read them through refs instead so startSearch is stable as long
  // as filters / sort / pagination / orgId / enabled stay the same.
  const getSalesRef = useRef<typeof getSales>(getSales);
  const refetchRef = useRef<typeof refetch>(refetch);
  const errorRef = useRef<typeof error>(error);
  // Keep latest-value refs in sync after each commit (read by the stable callback below).
  useEffect(() => {
    getSalesRef.current = getSales;
    refetchRef.current = refetch;
    errorRef.current = error;
  });

  const filterGroups = useMemo<
    FilterGroupOfSaleFilteredFilterOptionInput[]
  >(() => {
    const groups: FilterGroupOfSaleFilteredFilterOptionInput[] = [
      buildSaleSearchFilterGroup(filters, commonFilterOperator),
    ];

    const filter: FilterGroupOfSaleFilteredFilterOptionInput = {
      filters: [
        {
          field: FieldOption.societyId.value as SaleFilterField,
          operator: OperatorOption.equals.value as ComparisonOperator,
          value: orgId,
        },
      ],
      groups,
      logicalOperator: LogicalOperator.and,
    };

    const allowedTypes = typesForSaleTypeFilter(
      saleTypeFilterArray,
      saleDateFilter,
    );
    groups.push({
      filters: allowedTypes.map((value) => ({
        field: FieldOption.saleType.value as SaleFilterField,
        operator: OperatorOption.equals.value as ComparisonOperator,
        value,
      })),
      logicalOperator: LogicalOperator.or,
    });

    if (saleDateFilter === SALE_DATE_FILTER.CURRENT) {
      groups.push(
        buildCurrentSaleFilterGroup(format(new Date(), ISO_DATE_FORMAT)),
      );
    }

    return [filter];
  }, [
    commonFilterOperator,
    filters,
    orgId,
    saleDateFilter,
    saleTypeFilterArray,
  ]);

  const sortOptions = useMemo<SaleFilteredSortOptionInput[]>(
    () => buildSaleSortOptions(gridSortModel, saleDateFilter),
    [gridSortModel, saleDateFilter],
  );

  // Render from `data ?? previousData` so the grid keeps the prior rows
  // during refetches (filter / sort / pagination) instead of blanking.
  // See useMembersFilter for the full rationale: the old `loading ? [] :
  // …` pattern emptied the grid on every loading flip, producing the
  // visible "data being rerendered" jank on every server round-trip.
  const rows: GetSalesRow[] = useMemo(
    () =>
      (data ?? previousData)?.sales?.items.map(
        ({
          lotCount,
          saleDate,
          saleDateDescription,
          saleId,
          saleName,
          saleType,
        }) => ({
          ID: saleId,
          SALE_DATE: saleDate ? format(new Date(saleDate), dateFormat) : '',
          SALE_DATE_DESCRIPTION: saleDateDescription || '',
          SALE_NAME: saleName || '',
          LOT_COUNT: lotCount || 0,
          SALE_TYPE: saleType || '',
        }),
      ) || [],
    [data, previousData, dateFormat],
  );

  const startSearch = useCallback(() => {
    if (!enabled) return;

    const variables = {
      requestInput: {
        pagination: {
          currentPage: (paginationModel.page ?? 0) + 1,
          pageSize: paginationModel.pageSize,
        },
        filterGroups,
        sortOptions,
      },
    };
    const context = { headers: setDefaultHeader(orgId) };

    const currentError = errorRef.current;
    if (currentError && recoveredFromErrorRef.current !== currentError) {
      recoveredFromErrorRef.current = currentError;
      try {
        void refetchRef.current(variables);
      } catch {
        getSalesRef.current({ variables, context });
      }
      return;
    }
    getSalesRef.current({ variables, context });
  }, [
    enabled,
    filterGroups,
    orgId,
    // Depend on primitives, not the full paginationModel object —
    // PublicSearchInput's mount-time setPaginationModel({page:0,pageSize:…})
    // creates a new object reference even when values are unchanged, which
    // would otherwise invalidate startSearch and re-trigger
    // useStartSearchOnMount. Same reasoning as useMembersFilter.
    paginationModel.page,
    paginationModel.pageSize,
    sortOptions,
  ]);

  // Content signature of the request variables — de-dupes redundant fires
  // (StrictMode + stale-then-fresh society.settings churn) in
  // useStartSearchOnMount while still re-firing on real filter/sort/page
  // changes. See useStartSearchOnMount for the full rationale.
  const searchSignature = useMemo(
    () =>
      JSON.stringify({
        filterGroups,
        sortOptions,
        orgId,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
      }),
    [
      filterGroups,
      sortOptions,
      orgId,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  useStartSearchOnMount(startSearch, enabled, searchSignature);

  return useMemo(
    () => ({
      data: data?.sales,
      dataGrid: {
        columns: saleCatalogueColumns,
        rows,
        getRowId: (row: GetSalesRow) => row.ID,
      },
      done: (data || error) && !loading,
      error,
      loading,
      previousData: previousData?.sales,
      startSearch,
    }),
    [data, error, loading, rows, previousData?.sales, startSearch],
  );
}
